/**
 * Utility for generating API headers with proper authentication
 * Supports both traditional cookie auth and React Native x-go-auth-token
 * Note: Backend expects lowercase header names (e.g., 'x-go-auth-token' not 'X-GO-Auth-Token')
 */
export function createApiHeaders(additionalHeaders = {}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  // Add React Native auth token if available
  if (typeof window !== 'undefined') {
    // Client-side: check localStorage and global window object
    try {
      const authToken = localStorage.getItem('x-go-auth-token');
      if (authToken) {
        // Use lowercase header name to match backend expectations
        headers['x-go-auth-token'] = authToken;
        
        // Log for debugging (always log in dev tools - will help diagnose issues)
        console.log('Added auth token to request headers from localStorage');
      }
    } catch (e) {
      console.error('Failed to access localStorage for auth token');
    }
  }

  return headers;
}

/**
 * Store the React Native auth token for future API calls
 * @param token Authentication token from React Native headers
 */
export function storeAuthToken(token: string): void {
  if (typeof window !== 'undefined' && token) {
    try {
      localStorage.setItem('x-go-auth-token', token);
    } catch (e) {
      console.error('Failed to store auth token in localStorage');
    }
  }
}

/**
 * Get the current auth token from storage
 * @returns The stored auth token or null if not found
 */
export function getStoredAuthToken(): string | null {
  if (typeof window !== 'undefined') {
    try {
      return localStorage.getItem('x-go-auth-token');
    } catch (e) {
      console.error('Failed to retrieve auth token from localStorage');
    }
  }
  return null;
}

/**
 * Clear the stored auth token (e.g., on logout)
 */
export function clearAuthToken(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('x-go-auth-token');
    } catch (e) {
      console.error('Failed to clear auth token from localStorage');
    }
  }
}
